import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/i18n/i18n";
import { AuthProvider } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";
import AuthCallback from "@/auth/AuthCallback";
import ScrollToTop from "@/components/ScrollToTop";
import Header from "@/components/Header";
import HeroCarousel from "@/components/HeroCarousel";
import FeaturedPrograms from "@/components/FeaturedPrograms";
import UpcomingTrips from "@/components/UpcomingTrips";
import ClimbingSection from "@/components/ClimbingSection";
import FAQSection from "@/components/FAQSection";
import ArticlesSection from "@/components/ArticlesSection";
import StoriesSection from "@/components/StoriesSection";
import Footer from "@/components/Footer";
import UpcomingTripBanner from "@/components/UpcomingTripBanner";
import { Loader2 } from "lucide-react";
// Eager-load only the above-the-fold/critical pages; everything else is lazy
// so the initial bundle stays small.
const ExpeditionsPage = lazy(() => import("@/components/ExpeditionsPage"));
const ExpeditionDetailPage = lazy(() => import("@/components/ExpeditionDetailPage"));
const ClassesPage = lazy(() => import("@/components/ClassesPage"));
const ClassDetailPage = lazy(() => import("@/components/ClassDetailPage"));
// Iter285 — trip-alert one-click unsubscribe landing
const TripAlertUnsubscribe = lazy(() => import("@/pages/TripAlertUnsubscribe"));
const HymettusPage = lazy(() => import("@/components/HymettusPage"));
const HymettusClimbingPage = lazy(() => import("@/components/HymettusClimbingPage"));
const ResourcesPage = lazy(() => import("@/components/ResourcesPage"));
const ResourcesHubPage = lazy(() => import("@/components/ResourcesHubPage"));
const ResourceSectionPage = lazy(() => import("@/components/ResourceSectionPage"));
const ArchivePage = lazy(() => import("@/components/ArchivePage"));
const PrivacyPolicyPage = lazy(() => import("@/components/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("@/components/CookiePolicyPage"));
const DifficultyGradesPage = lazy(() => import("@/components/DifficultyGradesPage"));
const SearchPage = lazy(() => import("@/components/SearchPage"));
const AboutUsPage = lazy(() => import("@/components/AboutUsPage"));
const VolunteersPage = lazy(() => import("@/components/VolunteersPage"));
const AthletesPage = lazy(() => import("@/components/AthletesPage"));
const OurTeamPage = lazy(() => import("@/components/OurTeamPage"));
const HomeRecognitionWidget = lazy(() => import("@/components/HomeRecognitionSection"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const CompleteProfile = lazy(() => import("@/pages/CompleteProfile"));
const MemberPortal = lazy(() => import("@/pages/MemberPortal"));
const BookingStatus = lazy(() => import("@/pages/BookingStatus"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const NewsletterLanding = lazy(() => import("@/pages/NewsletterLanding"));
const AdminNewsletter = lazy(() => import("@/pages/AdminNewsletter"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminBlacklist = lazy(() => import("@/pages/AdminBlacklist"));
const AdminLanding = lazy(() => import("@/pages/AdminLanding"));
const AdminGuide = lazy(() => import("@/pages/AdminGuide"));
const AdminMemberDetail = lazy(() => import("@/pages/AdminMemberDetail"));
const AdminBankSlips = lazy(() => import("@/pages/AdminBankSlips"));
const AdminIdCards = lazy(() => import("@/pages/AdminIdCards"));
const AdminCMS = lazy(() => import("@/pages/AdminCMS"));
const AdminOperations = lazy(() => import("@/pages/AdminOperations"));
const AdminFinance = lazy(() => import("@/pages/AdminFinance"));
const AdminInventory = lazy(() => import("@/pages/AdminInventory"));
const AdminVolunteer = lazy(() => import("@/pages/AdminVolunteer"));
const AdminSystem = lazy(() => import("@/pages/AdminSystem"));
const LeaderRoster = lazy(() => import("@/pages/LeaderRoster"));
const ArticleDetailPage = lazy(() => import("@/pages/ArticleDetailPage"));
import { Toaster } from "@/components/ui/sonner";
import SEO from "@/components/SEO";

const PageFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
  </div>
);

const Home = () => {
  const origin = process.env.REACT_APP_CANONICAL_ORIGIN || 'https://eosh.gr';
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsOrganization',
    name: 'Ε.Ο.Σ. Ηλιούπολης',
    alternateName: 'Ilioupolis Alpine Club',
    url: origin,
    logo: `${origin}/logo512.png`,
    foundingDate: '1991',
    sport: ['Mountaineering', 'Climbing', 'Hiking'],
    areaServed: { '@type': 'Country', name: 'Greece' },
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO jsonLd={orgLd} />
      <Header />
      <UpcomingTripBanner />
      <main>
        <HeroCarousel />
        <FeaturedPrograms />
        <UpcomingTrips />
        <ClimbingSection />
        <FAQSection sidebar={<HomeRecognitionWidget />} />
        <ArticlesSection />
        <StoriesSection />
      </main>
      <Footer />
    </div>
  );
};

const Expeditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <UpcomingTripBanner />
      <ExpeditionsPage />
      <Footer />
    </div>
  );
};

const ExpeditionDetail = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <UpcomingTripBanner />
      <ExpeditionDetailPage />
      <Footer />
    </div>
  );
};

const Classes = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ClassesPage />
      <Footer />
    </div>
  );
};

const ClassDetail = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ClassDetailPage />
      <Footer />
    </div>
  );
};

const Hymettus = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <HymettusPage />
      <Footer />
    </div>
  );
};

const HymettusClimbing = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <HymettusClimbingPage />
      <Footer />
    </div>
  );
};

const Resources = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ResourcesHubPage />
      <Footer />
    </div>
  );
};

// Generic wrapper for /usefull/<section> — header/footer + section page.
const ResourcesSection = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <ResourceSectionPage />
    <Footer />
  </div>
);

const ResourcesArchive = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <ArchivePage />
    <Footer />
  </div>
);

const ResourcesCookiesPrivacy = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    {/* Legacy combined page route — kept alive for old bookmarks; renders the
        new dedicated Privacy Policy page (Cookies has its own /about/cookies). */}
    <PrivacyPolicyPage />
    <Footer />
  </div>
);

// Forward /resources/:section deep links to the canonical /usefull/:section.
const ResourcesSectionRedirect = () => {
  const { section } = useParams();
  return <Navigate to={`/usefull/${section || ''}`} replace />;
};

const AboutPrivacy = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <PrivacyPolicyPage />
    <Footer />
  </div>
);

const AboutCookies = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <CookiePolicyPage />
    <Footer />
  </div>
);

const ResourcesDifficultyGrades = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <DifficultyGradesPage />
    <Footer />
  </div>
);

// Legacy /resources-old route preserved for any deep links to the original
// PDF library; the new Usefull hub is at /usefull.
const ResourcesLegacy = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <ResourcesPage />
    <Footer />
  </div>
);

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <AboutUsPage />
      <Footer />
    </div>
  );
};

const Volunteers = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <VolunteersPage />
    <Footer />
  </div>
);

const Athletes = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <AthletesPage />
    <Footer />
  </div>
);

const AthletesOurTeam = () => (
  <div className="min-h-screen bg-gray-50">
    <Header />
    <OurTeamPage />
    <Footer />
  </div>
);

// Tiny helpers used by the legacy /ascents/:id and /courses/:id routes —
// forward to the canonical /expeditions/:id and /classes/:id while
// preserving the slug so deep links from external sites still resolve.
const AscentLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/expeditions/${id || ''}`} replace />;
};
const CourseLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/classes/${id || ''}`} replace />;
};

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/expeditions" element={<Expeditions />} />
        <Route path="/expeditions/:id" element={<ExpeditionDetail />} />
        {/* Legacy URL aliases — the nav reads "Ascents/Courses" but the
            internal routes use /expeditions and /classes. Direct visits or
            bookmarks to /ascents and /courses used to land on a blank page;
            redirect them to the live routes instead. */}
        <Route path="/ascents" element={<Navigate to="/expeditions" replace />} />
        <Route path="/ascents/:id" element={<AscentLegacyRedirect />} />
        <Route path="/classes" element={<Classes />} />
        <Route path="/classes/:id" element={<ClassDetail />} />
        <Route path="/courses" element={<Navigate to="/classes" replace />} />
        <Route path="/courses/:id" element={<CourseLegacyRedirect />} />
        <Route path="/athletes" element={<Navigate to="/athletes/our-team" replace />} />
        <Route path="/athletes/our-team" element={<AthletesOurTeam />} />
        <Route path="/athletes/become" element={<Athletes />} />
        <Route path="/trip-alerts/unsubscribe" element={<TripAlertUnsubscribe />} />
      <Route path="/hymettus" element={<Hymettus />} />
      <Route path="/hymettus/climbing" element={<HymettusClimbing />} />
      {/* Primary "Usefull" (intentional double L) information architecture. */}
      <Route path="/usefull" element={<Resources />} />
      <Route path="/usefull/library" element={<ResourcesLegacy />} />
      <Route path="/usefull/difficulty-grades" element={<ResourcesDifficultyGrades />} />
      <Route path="/usefull/archive" element={<ResourcesArchive />} />
      <Route path="/usefull/cookies-privacy" element={<ResourcesCookiesPrivacy />} />
      <Route path="/usefull/:section" element={<ResourcesSection />} />
      {/* Legacy /resources/* — preserved as 301-style client redirects so old
          bookmarks, SEO inbound links and existing emails still land safely
          on the renamed /usefull hub. */}
      <Route path="/resources" element={<Navigate to="/usefull" replace />} />
      <Route path="/resources/library" element={<Navigate to="/usefull/library" replace />} />
      <Route path="/resources/difficulty-grades" element={<Navigate to="/usefull/difficulty-grades" replace />} />
      <Route path="/resources/archive" element={<Navigate to="/usefull/archive" replace />} />
      <Route path="/resources/cookies-privacy" element={<Navigate to="/usefull/cookies-privacy" replace />} />
      <Route path="/resources/:section" element={<ResourcesSectionRedirect />} />
      <Route path="/about" element={<About />} />
      <Route path="/about/privacy" element={<AboutPrivacy />} />
      <Route path="/about/cookies" element={<AboutCookies />} />
      <Route path="/volunteers" element={<Volunteers />} />
      {/* Legacy URL — the Honor Wall now lives inside /volunteers, so any
          inbound link to /honors gracefully lands on the same content. */}
      <Route path="/honors" element={<Navigate to="/volunteers" replace />} />
      <Route path="/search" element={
        <div className="min-h-screen bg-gray-50">
          <Header />
          <SearchPage />
          <Footer />
        </div>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/booking-status/:bookingId" element={<BookingStatus />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/* Iter200 — Newsletter double-opt-in landing pages (token in query). */}
      <Route path="/newsletter/confirm" element={
        <div className="min-h-screen bg-gray-50">
          <Header />
          <NewsletterLanding mode="confirm" />
          <Footer />
        </div>
      } />
      <Route path="/newsletter/unsubscribe" element={
        <div className="min-h-screen bg-gray-50">
          <Header />
          <NewsletterLanding mode="unsubscribe" />
          <Footer />
        </div>
      } />
      <Route path="/newsletter/resubscribe" element={
        <div className="min-h-screen bg-gray-50">
          <Header />
          <NewsletterLanding mode="resubscribe" />
          <Footer />
        </div>
      } />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/complete-profile"
        element={<CompleteProfile />}
      />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <MemberPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminLanding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blacklist"
        element={
          <ProtectedRoute requireAdmin>
            <AdminBlacklist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guide"
        element={
          <ProtectedRoute requireAdmin>
            <AdminGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bank-slips"
        element={
          <ProtectedRoute requireAdmin>
            <AdminBankSlips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/id-cards"
        element={
          <ProtectedRoute requireAdmin>
            <AdminIdCards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletter"
        element={
          <ProtectedRoute requireAdmin>
            <AdminNewsletter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members/:user_id"
        element={
          <ProtectedRoute requireAdmin>
            <AdminMemberDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cms"
        element={
          <ProtectedRoute requireAdmin>
            <AdminCMS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operations"
        element={
          <ProtectedRoute requireAdmin>
            <AdminOperations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operations/:trip_id"
        element={
          <ProtectedRoute requireAdmin>
            <AdminOperations />
          </ProtectedRoute>
        }
      />
      {/* Mobile-first roster view for trip leaders + ops staff. The component
          self-gates by role; we wrap with ProtectedRoute so anonymous users get
          bounced to /login first. */}
      <Route
        path="/leader/:trip_id"
        element={
          <ProtectedRoute>
            <LeaderRoster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finance"
        element={
          <ProtectedRoute requireAdmin>
            <AdminFinance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/maintenance"
        element={<Navigate to="/admin/system#maintenance" replace />}
      />
      <Route
        path="/admin/inventory"
        element={
          <ProtectedRoute requireScope="inventory">
            <AdminInventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backups"
        element={<Navigate to="/admin/system#backups" replace />}
      />
      <Route
        path="/admin/system"
        element={
          <ProtectedRoute requireAdmin>
            <AdminSystem />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/volunteer"
        element={
          <ProtectedRoute requireAdmin>
            <AdminVolunteer />
          </ProtectedRoute>
        }
      />
      <Route path="/articles/:slug" element={
        <div className="min-h-screen bg-gray-50">
          <Header />
          <ArticleDetailPage />
          <Footer />
        </div>
      } />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
